import { useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { formatBRLFromCents, formatCount, VALOR_AUSENTE } from '../lib/format';
import type { DailyPoint } from '../types';

/**
 * Tendencia diaria (RNF15): carrinhos abandonados x carrinhos recuperados.
 *
 * Escolhas de visualizacao, na ordem em que foram tomadas:
 *
 * 1. FORMA. Duas series da mesma medida (contagem por dia) ao longo do tempo:
 *    linha. Sem area empilhada (nao sao partes de um todo) e sem segundo eixo
 *    (dinheiro fica na tooltip e na tabela, nunca numa escala paralela -- eixo
 *    duplo e o erro numero um de painel).
 *
 * 2. COR. Duas categorias = dois slots categoricos em ordem fixa: serie 1
 *    (abandonados) e serie 2 (recuperados), da paleta auditada em index.css.
 *    A identidade nunca e so cor: legenda com chave de linha e rotulo escrito.
 *
 * 3. MARCAS. Linha de 2px, ponto de 8px so no dia focado (com anel da
 *    superficie), grade de hairline solida e recessiva, sem numero em cada
 *    ponto -- os valores estao na tooltip e na tabela.
 *
 * 4. INTERACAO. Crosshair que encontra o X: o ponteiro mira num dia, nunca numa
 *    linha de 2px. Uma tooltip com TODAS as series daquele dia, valor em
 *    destaque e nome secundario. Teclado: setas andam pelos dias, com o mesmo
 *    conteudo. A tooltip nunca e a unica porta: a tabela esta a um clique.
 *
 * SVG com viewBox e preserveAspectRatio="none": a largura acompanha o
 * container sem recalculo em JavaScript; o texto fica FORA do SVG (HTML), para
 * nao esticar junto.
 */

export interface TrendChartProps {
  readonly title: string;
  readonly description: string;
  readonly points: readonly DailyPoint[];
}

const W = 720;
const H = 240;
const PAD_X = 8;
const PAD_TOP = 12;
const PAD_BOTTOM = 8;

const DIA_FORMATTER = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' });
const DIA_LONGO = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
const DIA_CURTO = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

/** YYYY-MM-DD -> Date local (meia-noite), sem o deslocamento UTC de new Date(str). */
function parseDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function niceMax(value: number): number {
  if (value <= 5) {
    return 5;
  }
  const pow = 10 ** Math.floor(Math.log10(value));
  const unit = value / pow;
  const step = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10;
  return step * pow;
}

export function TrendChart({ title, description, points }: TrendChartProps): JSX.Element {
  const [focus, setFocus] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tableId = useId();

  const geometry = useMemo(() => {
    const n = points.length;
    const max = niceMax(Math.max(0, ...points.map((p) => Math.max(p.abandonedCount, p.recoveredCount))));
    const innerW = W - PAD_X * 2;
    const innerH = H - PAD_TOP - PAD_BOTTOM;
    const x = (i: number): number => (n <= 1 ? W / 2 : PAD_X + (i * innerW) / (n - 1));
    const y = (v: number): number => PAD_TOP + innerH - (v / max) * innerH;
    const path = (pick: (p: DailyPoint) => number): string =>
      points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(pick(p)).toFixed(1)}`).join(' ');
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ value: Math.round(max * f), y: y(max * f) }));
    return { n, max, x, y, ticks, abandoned: path((p) => p.abandonedCount), recovered: path((p) => p.recoveredCount) };
  }, [points]);

  const vazio = points.length === 0 || points.every((p) => p.abandonedCount === 0 && p.recoveredCount === 0);

  const locate = (event: PointerEvent<SVGSVGElement>): void => {
    const svg = svgRef.current;
    if (svg === null || geometry.n === 0) {
      return;
    }
    const rect = svg.getBoundingClientRect();
    const rel = (event.clientX - rect.left) / rect.width;
    const idx = Math.round(rel * (geometry.n - 1));
    setFocus(Math.min(geometry.n - 1, Math.max(0, idx)));
  };

  const onKey = (event: KeyboardEvent<SVGSVGElement>): void => {
    if (geometry.n === 0) {
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const atual = focus ?? geometry.n - 1;
      const next = event.key === 'ArrowLeft' ? Math.max(0, atual - 1) : Math.min(geometry.n - 1, atual + 1);
      setFocus(next);
    } else if (event.key === 'Home') {
      setFocus(0);
    } else if (event.key === 'End') {
      setFocus(geometry.n - 1);
    } else if (event.key === 'Escape') {
      setFocus(null);
    }
  };

  const focado = focus === null ? null : points[focus] ?? null;
  // Rotulos do eixo X: no maximo ~6, para nao colidir em telas estreitas.
  const passo = Math.max(1, Math.ceil(geometry.n / 6));

  return (
    <figure className="flex flex-col gap-4 rounded-2xl border border-line bg-surface px-5 py-5 shadow-soft">
      <figcaption className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <p className="max-w-prose text-sm text-ink-muted">{description}</p>
        </div>
        <ul className="flex items-center gap-4 text-xs text-ink-muted" aria-label="Legenda">
          <li className="flex items-center gap-2">
            <span aria-hidden="true" className="h-0.5 w-4 rounded bg-series-1" />
            Abandonados
          </li>
          <li className="flex items-center gap-2">
            <span aria-hidden="true" className="h-0.5 w-4 rounded bg-series-2" />
            Recuperados
          </li>
        </ul>
      </figcaption>

      {vazio ? (
        <p className="py-10 text-center text-sm text-ink-subtle">Nenhum evento no período: a linha começa reta no zero.</p>
      ) : (
        <div className="relative">
          <div className="flex gap-2">
            {/* Eixo Y em HTML, alinhado pela mesma escala, para nao esticar com o SVG. */}
            <div className="relative w-8 shrink-0 text-right text-[10px] tabular-nums text-ink-subtle" style={{ height: `${H}px` }} aria-hidden="true">
              {geometry.ticks.map((t) => (
                <span key={t.value} className="absolute right-0 -translate-y-1/2" style={{ top: `${(t.y / H) * 100}%` }}>
                  {formatCount(t.value)}
                </span>
              ))}
            </div>

            <div className="relative min-w-0 flex-1">
              <svg
                ref={svgRef}
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                className="block w-full cursor-crosshair touch-none outline-none focus-visible:ring-2 focus-visible:ring-brand"
                style={{ height: `${H}px` }}
                role="img"
                aria-label={`${title}. Use as setas para percorrer os dias; a tabela abaixo tem os valores.`}
                tabIndex={0}
                onPointerMove={locate}
                onPointerDown={locate}
                onPointerLeave={() => setFocus(null)}
                onKeyDown={onKey}
                onBlur={() => setFocus(null)}
              >
                {geometry.ticks.map((t) => (
                  <line key={t.value} x1={0} x2={W} y1={t.y} y2={t.y} className="stroke-line" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                ))}

                <path d={geometry.abandoned} className="stroke-series-1" strokeWidth={2} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                <path d={geometry.recovered} className="stroke-series-2" strokeWidth={2} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />

                {focus !== null && focado !== null ? (
                  <g>
                    <line x1={geometry.x(focus)} x2={geometry.x(focus)} y1={PAD_TOP} y2={H - PAD_BOTTOM} className="stroke-edge" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                    <circle cx={geometry.x(focus)} cy={geometry.y(focado.abandonedCount)} r={4} className="fill-series-1 stroke-surface" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                    <circle cx={geometry.x(focus)} cy={geometry.y(focado.recoveredCount)} r={4} className="fill-series-2 stroke-surface" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                  </g>
                ) : null}
              </svg>

              {focus !== null && focado !== null ? (
                <Tooltip point={focado} leftPercent={(geometry.x(focus) / W) * 100} />
              ) : null}

              <div className="mt-1 flex justify-between text-[10px] tabular-nums text-ink-subtle" aria-hidden="true">
                {points.map((p, i) =>
                  i % passo === 0 || i === geometry.n - 1 ? (
                    <span key={p.day}>{DIA_FORMATTER.format(parseDay(p.day))}</span>
                  ) : null,
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <details>
        <summary className="cursor-pointer text-sm font-medium text-ink-muted transition-colors hover:text-ink">
          Ver os números por dia em tabela
        </summary>
        <div className="mt-3 max-h-72 overflow-auto">
          <table id={tableId} className="w-full min-w-[30rem] border-collapse text-sm">
            <caption className="sr-only">Carrinhos abandonados e recuperados por dia, com valores</caption>
            <thead className="sticky top-0 bg-surface">
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
                <th scope="col" className="py-2 pr-4 font-medium">Dia</th>
                <th scope="col" data-numeric="true" className="py-2 pr-4 text-right font-medium">Abandonados</th>
                <th scope="col" data-numeric="true" className="py-2 pr-4 text-right font-medium">Valor abandonado</th>
                <th scope="col" data-numeric="true" className="py-2 pr-4 text-right font-medium">Contatados</th>
                <th scope="col" data-numeric="true" className="py-2 pr-4 text-right font-medium">Recuperados</th>
                <th scope="col" data-numeric="true" className="py-2 text-right font-medium">Valor recuperado</th>
              </tr>
            </thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.day} className="border-b border-line/60 last:border-0">
                  <th scope="row" className="py-2 pr-4 text-left font-normal text-ink">{DIA_CURTO.format(parseDay(p.day))}</th>
                  <td data-numeric="true" className="py-2 pr-4 text-right text-ink">{formatCount(p.abandonedCount)}</td>
                  <td data-numeric="true" className="py-2 pr-4 text-right text-ink-muted">{p.abandonedAmountCents > 0 ? formatBRLFromCents(p.abandonedAmountCents) : VALOR_AUSENTE}</td>
                  <td data-numeric="true" className="py-2 pr-4 text-right text-ink-muted">{formatCount(p.contactedCount)}</td>
                  <td data-numeric="true" className="py-2 pr-4 text-right text-ink">{formatCount(p.recoveredCount)}</td>
                  <td data-numeric="true" className="py-2 text-right text-ink-muted">{p.recoveredAmountCents > 0 ? formatBRLFromCents(p.recoveredAmountCents) : VALOR_AUSENTE}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

function Tooltip({ point, leftPercent }: { readonly point: DailyPoint; readonly leftPercent: number }): JSX.Element {
  // Tooltip vira de lado perto das bordas para nao sair do cartao.
  const alinhaEsquerda = leftPercent > 60;
  return (
    <div
      role="status"
      className="pointer-events-none absolute top-2 z-10 w-56 rounded-xl border border-line bg-elevated p-3 text-xs shadow-float"
      style={alinhaEsquerda ? { right: `${100 - leftPercent + 1.5}%` } : { left: `${leftPercent + 1.5}%` }}
    >
      <p className="mb-2 font-medium capitalize text-ink">{DIA_LONGO.format(parseDay(point.day))}</p>
      <dl className="flex flex-col gap-1.5">
        <TooltipRow tone="bg-series-1" value={formatCount(point.abandonedCount)} label="abandonados" detail={formatBRLFromCents(point.abandonedAmountCents)} />
        <TooltipRow tone="bg-series-2" value={formatCount(point.recoveredCount)} label="recuperados" detail={formatBRLFromCents(point.recoveredAmountCents)} />
        <TooltipRow tone="bg-ink-subtle" value={formatCount(point.contactedCount)} label="contatados" detail={`${formatCount(point.voiceCalls)} ligações · ${formatCount(point.whatsappSent)} WhatsApp`} />
      </dl>
    </div>
  );
}

function TooltipRow({ tone, value, label, detail }: { readonly tone: string; readonly value: string; readonly label: string; readonly detail: string }): JSX.Element {
  return (
    <div className="flex items-start gap-2">
      <span aria-hidden="true" className={`mt-1.5 h-0.5 w-3 shrink-0 rounded ${tone}`} />
      <div className="min-w-0 flex-1">
        <dt className="sr-only">{label}</dt>
        <dd className="flex items-baseline justify-between gap-2">
          <span className="font-semibold tabular-nums text-ink">{value}</span>
          <span className="text-ink-muted">{label}</span>
        </dd>
        <dd className="truncate text-ink-subtle">{detail}</dd>
      </div>
    </div>
  );
}
